import React, { useState, useEffect } from 'react'
import Modal from './Modal'

interface Message {
  id: number
  subject: string
  content: string
  message_type: string
  sent_at: string
  recipients_count: number
  created_by: {
    id: number
    name: string
  } | null
}

interface MessageHistoryProps {
  clubId: number
  onClose: () => void
}

export default function MessageHistory({ clubId, onClose }: MessageHistoryProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchMessages()
  }, [clubId])

  const fetchMessages = async () => {
    try {
      const response = await fetch(`/api/v1/clubs/${clubId}/messages`)
      if (!response.ok) throw new Error('Failed to fetch messages')

      const data = await response.json()
      setMessages(data.messages || [])
    } catch (err) {
      console.error('Error fetching messages:', err)
      setError(err instanceof Error ? err.message : 'Failed to load messages')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const getMessageTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      announcement: 'Announcement',
      reminder: 'Reminder',
      custom: 'Custom'
    }
    return labels[type] || type
  }

  const getMessageTypeBadgeColor = (type: string) => {
    const colors: Record<string, string> = {
      announcement: 'bg-blue-100 text-blue-700',
      reminder: 'bg-yellow-100 text-yellow-700',
      custom: 'bg-purple-100 text-purple-700'
    }
    return colors[type] || 'bg-gray-100 text-gray-700'
  }

  if (loading) {
    return (
      <Modal isOpen={true} onClose={onClose} size="xl" showCloseButton={true}>
        <div className="p-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal isOpen={true} onClose={onClose} size="xl" showCloseButton={true} className="overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Message History</h2>
      </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {!error && messages.length === 0 && (
            <div className="p-12 text-center">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No messages yet</h3>
              <p className="text-gray-600">
                Send your first message to get started!
              </p>
            </div>
          )}

          {!error && messages.length > 0 && !selectedMessage && (
            <div className="p-6 space-y-4">
              {messages.map((message) => (
                <button
                  key={message.id}
                  onClick={() => setSelectedMessage(message)}
                  className="w-full text-left bg-white border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 flex-1">{message.subject}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${getMessageTypeBadgeColor(message.message_type)}`}>
                      {getMessageTypeLabel(message.message_type)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                    {message.content}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{formatDate(message.sent_at)}</span>
                    <span>•</span>
                    <span>{message.recipients_count} recipients</span>
                    {message.created_by && (
                      <>
                        <span>•</span>
                        <span>Sent by {message.created_by.name}</span>
                      </>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedMessage && (
            <div className="p-6">
              <button
                onClick={() => setSelectedMessage(null)}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to list
              </button>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="mb-4">
                  <span className={`inline-block text-xs px-2 py-1 rounded-full ${getMessageTypeBadgeColor(selectedMessage.message_type)}`}>
                    {getMessageTypeLabel(selectedMessage.message_type)}
                  </span>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">{selectedMessage.subject}</h2>

                <div className="flex items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-200">
                  <span>{formatDate(selectedMessage.sent_at)}</span>
                  <span>•</span>
                  <span>{selectedMessage.recipients_count} recipients</span>
                  {selectedMessage.created_by && (
                    <>
                      <span>•</span>
                      <span>Sent by {selectedMessage.created_by.name}</span>
                    </>
                  )}
                </div>

                <div className="prose prose-gray max-w-none">
                  <div className="whitespace-pre-wrap text-gray-700">{selectedMessage.content}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
  )
}
